import Link from "ui/Link";
import styled from "styled-components/macro";
import logout from "utils/logout";
import { theme } from "ui/theme";
import { useCookieSettings } from "components/CookieSettings";

type FooterStyleProps = {
  $isOnDarkBackground?: boolean;
};
const $FooterWrapper = styled.footer<FooterStyleProps>`
  border: 0;
  margin-top: 2rem;
  padding: 3rem 0;
  text-align: center;
  position: relative;
`;

const $FooterLinks = styled.ul<FooterStyleProps>`
  padding: 0;
  list-style-type: none;
  display: flex;
  flex-wrap: no-wrap;
  justify-content: center;
  color: ${({ $isOnDarkBackground, theme }) =>
    $isOnDarkBackground ? theme.ColorWhite : theme.ColorBlurple500};

  a {
    text-decoration: none;
  }
  a:hover,
  a:focus,
  a:active {
    color: ${({ $isOnDarkBackground, theme }) =>
      $isOnDarkBackground ? theme.ColorBlurple200 : theme.ColorSpace500};
  }

  &.wrap {
    flex-wrap: wrap;
  }

  @media screen and (${theme.BreakpointLargeTabletDown}) {
    flex-wrap: wrap;
  }
`;

const $FooterLink = styled.li<FooterStyleProps>`
  padding: 0 1rem;
  border: 0;
  &:first-child {
    border: none;
  }

  @media screen and (${theme.BreakpointLargeTabletDown}) {
    padding: 0 1.5rem 0.5rem 1.5rem;
    margin-bottom: 1rem;

    &:last-child {
      padding-bottom: 0;
    }
  }
`;

enum FooterAction {
  SHOW_COOKIE_SETTINGS_MODAL = 1,
}

type FooterLinkType = {
  label: string;
  analyticsName: string;
  href?: string;
  clickAction?: FooterAction;
  opensInNewTab?: boolean;
};

const footerLinks = {
  teacher: [
    {
      label: "Privacy Policy",
      analyticsName: "clicked_privacy_policy_link",
      href: "/privacy-policy",
      opensInNewTab: true,
    },
    {
      label: "Student Privacy Notice",
      analyticsName: "clicked_student_privacy_notice_link",
      href: "/student-privacy-notice",
      opensInNewTab: true,
    },
    {
      label: "Cookie Settings",
      analyticsName: "clicked_cookies",
      clickAction: FooterAction.SHOW_COOKIE_SETTINGS_MODAL,
    },
    {
      label: "User Agreement",
      analyticsName: "clicked_user_agreement_link",
      href: "/user-agreement",
      opensInNewTab: true,
    },
    {
      label: "Code of Conduct",
      analyticsName: "clicked_code_of_conduct_link",
      href: "/code-of-conduct",
      opensInNewTab: true,
    },
  ],
  student: [
    {
      label: "Student Privacy Notice",
      analyticsName: "clicked_student_privacy_notice_link",
      href: "/student-privacy-notice",
      opensInNewTab: true,
    },
    {
      label: "Code of Conduct",
      analyticsName: "clicked_code_of_conduct_link",
      href: "/code-of-conduct",
      opensInNewTab: true,
    },
  ],
};

export type FooterProps = {
  className?: string;
  userType: "teacher" | "student";
  includeSignOut?: boolean;
  isOnDarkBackground?: boolean;
};

export default function Footer({
  className,
  userType,
  includeSignOut,
  isOnDarkBackground,
}: FooterProps) {
  const { showCookieSettings } = useCookieSettings();

  const onClickHandler = (action?: FooterAction) => {
    if (action === FooterAction.SHOW_COOKIE_SETTINGS_MODAL) {
      showCookieSettings();
    }
  };

  return (
    <$FooterWrapper className={className} data-testid="footer">
      <$FooterLinks $isOnDarkBackground={isOnDarkBackground}>
        {includeSignOut && (
          <$FooterLink>
            <Link
              analyticsName="logged_out"
              isOnDarkBackground={isOnDarkBackground}
              onClick={() => logout()}
            >
              Sign out
            </Link>
          </$FooterLink>
        )}

        {footerLinks[userType].map((link: FooterLinkType) => (
          <$FooterLink key={link.label.toLowerCase().replace(/ /g, "_")}>
            <Link
              analyticsName={link.analyticsName}
              opensInNewTab={link.opensInNewTab}
              href={link.href}
              onClick={() => {
                onClickHandler(link.clickAction);
              }}
              isOnDarkBackground={isOnDarkBackground}
            >
              {link.label}
            </Link>
          </$FooterLink>
        ))}
      </$FooterLinks>
    </$FooterWrapper>
  );
}
